/**
 * ConferenceBar (2026-08-10, rev2)
 * ================================
 * Avaya-style call-appearance list for the expandable (up to 4-party)
 * conference. Matches the 2-line MultiLineBar visual format: each appearance
 * is a row (line) the agent can see and act on.
 *
 * Appearances shown:
 *   - The CONFERENCE line (one row): "Conference (N)" while >=2 parties; the
 *     survivor's number once it collapses to a single remaining party. Actions
 *     on this line: Release me / End all. (Add Call / Hold / End also live in
 *     the normal bottom CallControls, Option A.)
 *   - The CONSULT line (second row, only when the agent stepped out to Add a
 *     party and it hasn't been merged yet): the new party's number, with
 *     Merge / Hold / Terminate + the ability to switch back to the conference.
 *
 * Props:
 *   partyCount, survivorNumber   conference line label inputs
 *   consultActive, consultNumber the pending 3rd line
 *   activeLine   : 'conference' | 'consult'   which line the agent is on
 *   onSwapToConference()  switch audio back to the conference
 *   onMergeParty()        merge the consult line into the conference
 *   onConsultHold()       hold the consult party, return to the conference
 *   onConsultHangup()     terminate the consult line, return to the conference
 *   onRelease()           Release me (leave; parties keep talking)
 *   onEndAll()            End the whole conference
 */
import React from 'react';

function LineRow({ icon, label, sub, active, onClick, actions }) {
  return (
    <div className={`cx-mlline ${active ? 'cx-mlline--active' : ''}`}>
      <button
        type="button"
        className="cx-mlline__pick"
        onClick={onClick}
        disabled={!onClick}
        title={label}
      >
        <span className="cx-mlline__who">{icon} {label}</span>
        <span className="cx-mlline__state">{sub}</span>
      </button>
      {actions}
    </div>
  );
}

export default function ConferenceBar({
  partyCount = 0,
  survivorNumber = null,
  consultActive = false,
  consultNumber = '',
  activeLine = 'conference',
  onSwapToConference,
  onMergeParty,
  onConsultHold,
  onConsultHangup,
  onRelease,
  onEndAll,
}) {
  const isConference = partyCount >= 2;
  const confLabel = isConference
    ? `Conference (${partyCount})`
    : (survivorNumber || 'Call');
  const confActive = !consultActive || activeLine === 'conference';

  return (
    <div className="cx-mlbar">
      <div className="cx-mlbar__title">
        {consultActive
          ? 'Click a line to talk · Merge to add to the conference'
          : 'Conference — one line'}
      </div>
      <div className="cx-mllines">
        {/* Conference line */}
        <LineRow
          icon={isConference ? '🧑\u200d🤝\u200d🧑' : '📞'}
          label={confLabel}
          sub={confActive ? '🎙️ talking' : '⏸ on hold'}
          active={confActive}
          onClick={consultActive && activeLine !== 'conference'
            ? () => onSwapToConference && onSwapToConference()
            : null}
          actions={
            <div className="cx-confbar__inline-actions">
              <button type="button" className="cx-mlline__txtbtn"
                onClick={() => onRelease && onRelease()}
                title="Release me — leave; the other parties keep talking">ReleaseMe</button>
              <button type="button" className="cx-mlline__txtbtn cx-mlline__txtbtn--danger"
                onClick={() => onEndAll && onEndAll()}
                title="End all — end the conference for everyone">END</button>
            </div>
          }
        />

        {/* Consult line (the new 3rd/4th party being added, pre-merge) */}
        {consultActive && (
          <LineRow
            icon="👤"
            label={consultNumber || 'New line…'}
            sub={activeLine === 'consult' ? '🎙️ talking' : '⏸ on hold'}
            active={activeLine === 'consult'}
            onClick={null}
            actions={
              <div className="cx-confbar__inline-actions">
                <button type="button" className="cx-mlline__txtbtn cx-mlline__txtbtn--merge"
                  onClick={() => onMergeParty && onMergeParty()}
                  title="Merge this line into the conference">Merge</button>
                <button type="button" className="cx-mlline__txtbtn"
                  onClick={() => onConsultHold && onConsultHold()}
                  title="Hold this line, return to the conference">HOLD</button>
                <button type="button" className="cx-mlline__txtbtn cx-mlline__txtbtn--danger"
                  onClick={() => onConsultHangup && onConsultHangup()}
                  title="Terminate this line, return to the conference">END</button>
              </div>
            }
          />
        )}
      </div>
    </div>
  );
}
